import { Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import saveAs from "file-saver";
import { forkJoin, map, of, tap } from "rxjs";

import { AdaaBoolean } from "../../../../../../constants/enums";
import type {
  CycleModelType,
  EntityModelType,
  OrgUnit,
  UserModelType,
  WorkflowDiffVersionType,
} from "../../../../../../models";
import { CyclesApiService, EntitiesApiService, FilesApiService, UsersApiService } from "../../../../../../services";
import {
  AccordionDiffViewComponent,
  AttachmentDiffViewComponent,
  ListDiffViewComponent,
  NormalDiffViewComponent,
} from "../../_lib";
import {
  AccordionSectionConfigType,
  CompositeSectionConfigType,
  ListSectionConfigType,
} from "../../wf-difference.types";
import { commonTpMilestoneScreenConfig } from "./tp-milestone-screen-config";

@Component({
  selector: "adaa-tp-milestone-screen",
  imports: [
    TranslatePipe,
    AttachmentDiffViewComponent,
    NormalDiffViewComponent,
    AccordionDiffViewComponent,
    ListDiffViewComponent,
  ],
  templateUrl: "./tp-milestone-screen.html",
  styleUrl: "./tp-milestone-screen.scss",
})
export class TpMilestoneScreenComponent implements OnInit {
  private readonly _usersApiService = inject(UsersApiService);
  private readonly _entitiesApiService = inject(EntitiesApiService);
  private readonly _cyclesApiService = inject(CyclesApiService);
  private readonly _filesApiService = inject(FilesApiService);

  showOnlyNew = input.required<boolean>();
  isNew = input<boolean>(false);
  newObject = input<WorkflowDiffVersionType>();
  oldObject = input<WorkflowDiffVersionType>();

  private readonly _newObject = computed<WorkflowDiffVersionType>(() => this.newObject() ?? {});
  private readonly _oldObject = computed<WorkflowDiffVersionType>(() => this.oldObject() ?? {});
  viewOnlyDifferences = input<boolean>(false);

  isScreenReady = signal<boolean>(false);
  cycles = signal<CycleModelType[]>([]);
  entities = signal<EntityModelType[]>([]);
  owners = signal<UserModelType[]>([]);
  orgUnits = signal<OrgUnit[]>([]);

  newData = computed(() => {
    const owners = this.owners();
    if (owners && owners.length < 1) return this._newObject();

    let object = this._newObject();

    if ("owner" in object) {
      const item = owners.find(({ id }) => id === object.owner);
      if (!item) return object;

      object = {
        ...object,
        ownerNameAE: item.nameAE,
        ownerNameEN: item.nameEN,
      };
    }

    return object;
  });
  oldData = computed(() => {
    const owners = this.owners();
    if (owners && owners.length < 1) return this._oldObject();

    let object = this._oldObject();

    if ("owner" in object) {
      const item = owners.find(({ id }) => id === object.owner);
      if (!item) return object;

      object = {
        ...object,
        ownerNameAE: item.nameAE,
        ownerNameEN: item.nameEN,
      };
    }

    return object;
  });

  readonly config = commonTpMilestoneScreenConfig;
  readonly $asAccordionConfig = ($s: CompositeSectionConfigType) => $s as AccordionSectionConfigType;
  readonly $asListConfig = ($s: CompositeSectionConfigType) => $s as ListSectionConfigType;

  readonly #entityIds = (): number[] => {
    const newEntityId = this._newObject().entityId;
    const oldEntityId = this._oldObject().entityId;
    const ids: number[] = [];

    if (newEntityId) ids.push(newEntityId);
    if (oldEntityId && oldEntityId !== newEntityId) ids.push(oldEntityId);

    return ids;
  };

  readonly #getOwners = () => {
    const entityIds = this.#entityIds();
    if (entityIds.length === 0) return of([]);

    const getOwnersPerEntity$ = entityIds.map((entityId) => this._usersApiService.getOwnersPerEntity(entityId));

    return forkJoin(getOwnersPerEntity$).pipe(
      map((res) => {
        const list = [];
        for (const { responseData } of res) {
          list.push(...(responseData ?? []));
        }
        return list;
      }),
      tap((data) => this.owners.set(data))
    );
  };
  readonly #apiRequests = () => {
    const api$ = [this._cyclesApiService.getAll(), this._entitiesApiService.getAll(AdaaBoolean.Y, AdaaBoolean.N)];
    const obs$ = forkJoin(api$).pipe(
      tap({
        next: ([cycles, entities]) => {
          this.cycles.set(cycles.responseData as CycleModelType[]);
          this.entities.set(entities.responseData as EntityModelType[]);
        },
      })
    );

    return forkJoin([obs$, this.#getOwners()]).subscribe({
      next: () => {
        this.isScreenReady.set(true);
      },
    });
  };

  public ngOnInit() {
    this.#apiRequests();
  }

  public downloadFile(data: Record<string, unknown>) {
    data.downloading = true;
    this._filesApiService.download(data.id as number, true).subscribe({
      next: (blob) => {
        saveAs(blob, data.filename as string);
      },
      error: () => {
        data.downloading = false;
      },
      complete: () => {
        data.downloading = false;
      },
    });
  }
}
